import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Toast from './Toast'
import styles from './Toast.module.scss'

const MAX_TOASTS = 5
const DEFAULT_DURATION = 4000

const ToastContext = createContext(null)

/**
 * useToast hook returns:
 *   { success, error, info, warning, promise(fn, {loading, success, error}) }
 */
export const useToast = () => {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Graceful no-op so calling sites don't crash before provider mounts.
    const noop = () => undefined
    return {
      success: noop,
      error: noop,
      info: noop,
      warning: noop,
      promise: async (fn) => (typeof fn === 'function' ? fn() : fn),
    }
  }
  return ctx
}

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([])
  const idRef = useRef(0)

  const remove = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id))
  }, [])

  const push = useCallback((variant, message, opts = {}) => {
    const id = ++idRef.current
    setToasts((list) => {
      const next = [...list, { id, variant, message, duration: opts.duration ?? DEFAULT_DURATION }]
      // Cap stack: drop oldest while above MAX_TOASTS.
      while (next.length > MAX_TOASTS) next.shift()
      return next
    })
    return id
  }, [])

  const api = useMemo(() => {
    const success = (msg, opts) => push('success', msg, opts)
    const error = (msg, opts) => push('error', msg, opts)
    const info = (msg, opts) => push('info', msg, opts)
    const warning = (msg, opts) => push('warning', msg, opts)
    const promise = async (fn, msgs = {}) => {
      const loadingId = msgs.loading ? push('info', msgs.loading, { duration: 0 }) : null
      try {
        const result = typeof fn === 'function' ? await fn() : await fn
        if (loadingId) remove(loadingId)
        if (msgs.success) {
          const text = typeof msgs.success === 'function' ? msgs.success(result) : msgs.success
          push('success', text)
        }
        return result
      } catch (err) {
        if (loadingId) remove(loadingId)
        if (msgs.error) {
          const text = typeof msgs.error === 'function' ? msgs.error(err) : msgs.error
          push('error', text)
        }
        throw err
      }
    }
    return { success, error, info, warning, promise }
  }, [push, remove])

  const portal = typeof document !== 'undefined'
    ? createPortal(
        <div className={styles.container} aria-live="polite" aria-atomic="false">
          {toasts.map((t) => (
            <Toast
              key={t.id}
              id={t.id}
              message={t.message}
              variant={t.variant}
              duration={t.duration}
              onClose={remove}
            />
          ))}
        </div>,
        document.body,
      )
    : null

  return (
    <ToastContext.Provider value={api}>
      {children}
      {portal}
    </ToastContext.Provider>
  )
}

export default ToastProvider
